import React, { useMemo, ReactElement } from "react";
import styles from './ContactsBlock.module.scss';
import { YMap } from "@kursk/components/ui/YMap/YMap";
import { IHotel, IPlace, IPlaceScheduleGroup } from "@common/redux/types/places";
import { getAddress } from "@kursk/utils/getAddress";
import { ContactType } from "@common/redux/types/common";
import {
  FacebookIcon,
  InstagramIcon,
  OkIcon,
  SkypeIcon,
  TelegramIcon,
  TwitterIcon,
  VkIcon,
} from "@kursk/components/ui/icons";
import { normalizePhone, prettifyPhone } from "@common/utils/phones";
import { useOrientations } from "@common/hooks/useOrientation";
import { ShowMoreWithToolTip } from "@kursk/components/ui/ShowMoreWithToolTip/ShowMoreWithToolTip";

interface IProp {
  entity: IPlace | IHotel;
}

type ContactsDataType = {
  [p in ContactType]?: string[];
}

const socialIconsHash = {
  vk: <VkIcon width={25}/>,
  ok: <OkIcon width={25}/>,
  twitter: <TwitterIcon width={25}/>,
  facebook: <FacebookIcon width={25}/>,
  instagram: <InstagramIcon width={25}/>,
  skype: <SkypeIcon width={25}/>,
  telegram: <TelegramIcon width={25}/>,
}

export const ContactsBlock = ({ entity }: IProp): ReactElement => {
  const orientation = useOrientations();

  const contactsData = useMemo((): ContactsDataType => {
    const data = {};
    entity.contacts.forEach((item) => {
      if (data[item.type]) {
        data[item.type].push(item[item.type]);
      } else {
        data[item.type] = [item[item.type]];
      }
    })
    return data;
  }, [entity]);

  const socialData = Object.keys(contactsData).filter((item) => !!socialIconsHash[item]);

  return (
    <div className={styles.contacts}>
      <div className={styles.contacts_title}>Контакты</div>
      <div className={styles.contacts_content}>
        <div className={styles.contacts_mapWrapper}>
          <YMap mapPosition={entity.mapPosition} placeName={entity.name}/>
        </div>
        <div className={styles.contacts_contactsWrapper}>
          <div className={styles.contacts_infoWrapper}>
            <div className={styles.contacts_addressWrapper}>
              <div className={styles.contacts_subTitle}>Адрес:</div>
              <div className={styles.contacts_address}>{getAddress(entity.address)}</div>
              {orientation === "desktop" &&
              <ShowMoreWithToolTip
                text={entity.address.comment}
              />}
              {orientation !== "desktop" && entity.address.comment &&
              <div className={styles.contacts_comment}>{entity.address.comment}</div>}
            </div>
            {contactsData.phone && (
              <div className={styles.contacts_telephone}>
                <div className={styles.contacts_subTitle}>Телефон:</div>
                {contactsData.phone.map((item, index) => (
                  <div className={styles.contacts_phone} key={`${item}_${index}`}>
                    <a href={'tel:' + normalizePhone(item)}>
                      {prettifyPhone(item)}
                    </a>
                  </div>
                ))}
              </div>
            )}
            {(contactsData.email || contactsData.website) && (
              <div className={styles.contacts_links}>
                {contactsData.email && contactsData.email.map((item, index) => (
                  <div className={styles.contacts_link} key={`${item}_${index}`}>
                    <a href={'mailto:' + item}>
                      {item}
                    </a>
                  </div>
                ))}
                {contactsData.website && contactsData.website.map((item, index) => (
                  <div className={styles.contacts_link} key={`${item}_${index}`}>
                    <a href={item} target='_blank' rel="noreferrer">
                      {item.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.contacts_socials}>
              {socialData.map((item) => contactsData[item].map((contactsItem, index) => (
                <a
                  href={contactsItem}
                  key={index}
                  className={styles.contacts_socialLink}
                  target='_blank'
                  rel="noreferrer"
                >
                  {socialIconsHash[item]}
                </a>
              )))}
            </div>
          </div>
          {entity?.scheduleGroups.length > 0 && <div className={styles.contacts_timetableWrapper}>
            <div className={styles.contacts_timetable}>
              <div className={styles.contacts_subTitle}>Режим работы:</div>
              {entity?.scheduleGroups?.map((item: IPlaceScheduleGroup, index) => (
                <div className={styles.contacts_schedule} key={index}>
                  <span>{item.days}</span><span>{item.times}</span>
                </div>
              ))}
              {orientation === "desktop" &&
              <ShowMoreWithToolTip
                text={entity.scheduleComment}
              />}
              {orientation !== "desktop" && entity.scheduleComment && <div className={styles.contacts_comment}>
                {entity.scheduleComment}
              </div>}
            </div>
          </div>
          }
        </div>
      </div>
    </div>
  )
}
